import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Landmark } from 'lucide-react'
import './FundsList.css'
import {
  PageHeader, SectionCard, Card, Badge, EmptyState, KpiTile, KpiGrid,
  DataTable, ListToolbar, useViewMode, type Column,
} from '@/components/ui'
import { useStore } from '@/store/useStore'
import { accountById, propertiesForFund, latestRevenue } from './fundUtils'
import { money, num } from '@/lib/format'
import type { Dataset, Fund } from '@/types/entities'

interface FundRow {
  fund: Fund
  investorName: string
  investorId?: string
  propertyCount: number
  inPlaceRevenue: number // annualized in-place revenue across assigned properties
}

function buildRows(data: Dataset): FundRow[] {
  return data.funds.map((fund) => {
    const props = propertiesForFund(data, fund.id)
    const investor = accountById(data, fund.investorAccountIds[0])
    const monthlyRev = props.reduce((s, p) => s + latestRevenue(p), 0)
    return {
      fund,
      investorName: investor?.name ?? '—',
      investorId: investor?.id,
      propertyCount: props.length,
      inPlaceRevenue: monthlyRev * 12, // annualize latest monthly snapshot
    }
  })
}

export default function FundsList() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useViewMode('funds', 'cards')

  const rows = useMemo(() => buildRows(data), [data])
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      r.fund.name.toLowerCase().includes(q) ||
      r.investorName.toLowerCase().includes(q) ||
      (r.fund.strategy?.toLowerCase().includes(q) ?? false),
    )
  }, [rows, search])

  const totalProps = filtered.reduce((s, r) => s + r.propertyCount, 0)
  const totalRev = filtered.reduce((s, r) => s + r.inPlaceRevenue, 0)
  const investors = new Set(filtered.map((r) => r.investorId).filter(Boolean)).size

  const cols: Column<FundRow>[] = [
    {
      key: 'name',
      header: 'Fund',
      sortValue: (r) => r.fund.name,
      render: (r) => (
        <Link to={`/funds/${r.fund.id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>
          {r.fund.name}
        </Link>
      ),
    },
    {
      key: 'vintage',
      header: 'Vintage',
      sortValue: (r) => r.fund.vintage ?? '',
      render: (r) => r.fund.vintage ? <span className="muted">{r.fund.vintage}</span> : <span className="dim">—</span>,
    },
    {
      key: 'investor',
      header: 'Investor Account',
      sortValue: (r) => r.investorName,
      render: (r) => r.investorId
        ? <Link to={`/accounts/${r.investorId}`} onClick={(e) => e.stopPropagation()}>{r.investorName}</Link>
        : '—',
    },
    {
      key: 'strategy',
      header: 'Strategy',
      sortValue: (r) => r.fund.strategy ?? '',
      render: (r) => <span className="muted truncate" style={{ display: 'block', maxWidth: 320 }}>{r.fund.strategy ?? '—'}</span>,
    },
    {
      key: 'props',
      header: 'Properties',
      align: 'right',
      sortValue: (r) => r.propertyCount,
      render: (r) => <span className="num">{num(r.propertyCount)}</span>,
    },
    {
      key: 'rev',
      header: 'In-Place Revenue',
      align: 'right',
      sortValue: (r) => r.inPlaceRevenue,
      render: (r) => <span className="num">{r.inPlaceRevenue > 0 ? money(r.inPlaceRevenue, { compact: true }) : '—'}</span>,
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Capital"
        title={<>Funds &amp; <b>Vehicles</b></>}
        sub="Investor fund structures and joint ventures, with their assigned self-storage portfolios."
      />

      <KpiGrid cols={4}>
        <KpiTile label="Active Vehicles" value={filtered.length} sub="funds + JV" />
        <KpiTile label="Investor Accounts" value={investors} sub="distinct LPs" />
        <KpiTile label="Assigned Properties" value={num(totalProps)} sub="across all vehicles" />
        <KpiTile label="In-Place Revenue" value={money(totalRev, { compact: true })} accent sub="annualized run-rate" />
      </KpiGrid>

      {filtered.length === 0 ? (
        <SectionCard pad>
          <EmptyState icon={<Landmark size={36} />} title="No funds match" hint="Clear your search to see all vehicles." />
        </SectionCard>
      ) : (
        <>
          <ListToolbar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Search funds, investors, strategy…"
            count={filtered.length}
            addLabel="New Fund"
            onAdd={() => {}}
            onExport={() => {}}
            viewMode={viewMode}
            onViewMode={setViewMode}
          />

          {viewMode === 'cards' ? (
            <div className="list-card-grid">
              {filtered.map((r) => (
                <Card key={r.fund.id} className="fund-card" onClick={() => navigate(`/funds/${r.fund.id}`)}>
                  <div className="fund-card-head">
                    <div>
                      <Link
                        to={`/funds/${r.fund.id}`}
                        className="fund-card-name"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.fund.name}
                      </Link>
                      <div className="fund-card-acct">
                        {r.investorId
                          ? <Link to={`/accounts/${r.investorId}`} onClick={(e) => e.stopPropagation()}>{r.investorName}</Link>
                          : r.investorName}
                      </div>
                    </div>
                    {r.fund.vintage && <Badge tone="navy" variant="outline">{r.fund.vintage}</Badge>}
                  </div>
                  {r.fund.strategy && <p className="fund-card-strategy">{r.fund.strategy}</p>}
                  <div className="fund-card-stats">
                    <div className="fund-card-stat">
                      <span className="fund-card-stat-label">Properties</span>
                      <span className="fund-card-stat-val">{num(r.propertyCount)}</span>
                    </div>
                    <div className="fund-card-stat">
                      <span className="fund-card-stat-label">In-Place Rev</span>
                      <span className="fund-card-stat-val">{r.inPlaceRevenue > 0 ? money(r.inPlaceRevenue, { compact: true }) : '—'}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <SectionCard title={<>Vehicle <b>Rollup</b></>} eyebrow="All funds" pad={false}>
              <DataTable<FundRow>
                columns={cols}
                rows={filtered}
                getRowKey={(r) => r.fund.id}
                onRowClick={(r) => navigate(`/funds/${r.fund.id}`)}
                initialSort={{ key: 'rev', dir: 'desc' }}
                emptyText="No funds."
              />
            </SectionCard>
          )}
        </>
      )}
    </>
  )
}
